import React from 'react';
import moment from 'moment';
import IntervalRenderer from 'flow/components/custom-components/interval-renderer';
import useAppContext from 'flow/hooks/useAppContext';
import { getPrettyDuration, getRedirectNumber } from 'flow/utils/call-utils';
import { getCallItemContactTitle } from 'flow/utils/contact-utils';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import CoListeningContactItem from './CoListeningContactItem';
import IconButton from '../ui-components/IconButton';
import Icon from '../ui-components/Icon';
import RedirectPresentationWidget from './RedirectPresentationWidget';

interface Props {
    subjectContact: TypedMap<ContactFull>;
    subjectNumber: string;
    remotePartyOfSubjectContact: TypedMap<ContactFull>;
    remotePartyOfSubjectNumber: string;
    startTime: Date;
    ownCountryCode: string;
    //TODO: #typeFix - WEB-14418 - Type this when we start working with Remote calls https://telavox.atlassian.net/browse/WWEB-14775
    activeCallOfSubject: any;
    handleChat: () => void;
    onHangup: () => void;
}

const CoListeningSupervisorLarge = ({
    subjectContact,
    subjectNumber,
    remotePartyOfSubjectContact,
    remotePartyOfSubjectNumber,
    startTime,
    ownCountryCode,
    activeCallOfSubject,
    handleChat,
    onHangup,
}: Props) => {
    const { i18next } = useAppContext();
    const agentName = getCallItemContactTitle(
        subjectContact,
        null,
        subjectNumber,
        ownCountryCode,
        i18next,
    );
    const customerName = getCallItemContactTitle(
        remotePartyOfSubjectContact,
        null,
        remotePartyOfSubjectNumber,
        ownCountryCode,
        i18next,
    );
    const showRoutingItem =
        activeCallOfSubject &&
        (activeCallOfSubject.get('queue') ||
            getRedirectNumber(activeCallOfSubject));

    return (
        <div className="w-full rounded-sm bg-gray-800">
            <div className="bg-yellow mb-5 flex justify-center truncate rounded-t-sm py-3 font-bold text-gray-800">
                {i18next.t('callcontrol.colistening.listening.counter')}
                <IntervalRenderer interval={1000}>
                    {() =>
                        getPrettyDuration(
                            moment.duration(moment().diff(moment(startTime))),
                        )
                    }
                </IntervalRenderer>
            </div>
            <CoListeningContactItem
                contact={subjectContact}
                contactName={agentName.text}
                size="large"
                handleChat={handleChat}
            />

            <span className="flex p-3 text-white">
                {i18next.t('callcontrol.colistening.talking.to')}
            </span>

            <CoListeningContactItem
                contact={remotePartyOfSubjectContact}
                contactName={customerName.text}
                size="large"
            />

            {showRoutingItem && (
                <>
                    <span className="flex p-3 text-white">
                        {i18next.t('settings.callrouting.title')}
                    </span>
                    <div className="my-2">
                        <RedirectPresentationWidget
                            activeCall={activeCallOfSubject}
                        />
                    </div>
                </>
            )}
            <div className="mb-3 flex justify-center">
                <div className="flex flex-col items-center p-5">
                    <IconButton
                        onClick={onHangup}
                        className="hover:darken bg-red text-white"
                        data-test-id="abort-button"
                        tooltipLabel={i18next.t(
                            'callcontrol.colistening.stop.listening',
                        )}
                    >
                        <Icon iconKey="close" />
                    </IconButton>
                    <span className="mt-2 text-xs text-white">
                        {i18next.t('callcontrol.colistening.stop.listening')}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CoListeningSupervisorLarge;
